import { Badge } from '../components/ui/badge';

/**
 * Get status badge configuration
 * @param {string} status - The status value
 * @returns {Object} - Badge configuration
 */
export const getStatusBadge = (status) => {
  const statusConfig = {
    confirmed: { variant: "default", text: "Confirmed" },
    pending: { variant: "secondary", text: "Pending" },
    completed: { variant: "outline", text: "Completed" },
    cancelled: { variant: "destructive", text: "Cancelled" },
    active: { variant: "default", text: "Active" },
    inactive: { variant: "secondary", text: "Inactive" }
  };
  
  const config = statusConfig[status] || { variant: "secondary", text: status };
  return <Badge variant={config.variant}>{config.text}</Badge>;
};

/**
 * Format date to readable string
 * @param {string} dateString - Date string to format
 * @returns {string} - Formatted date
 */
export const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

/**
 * Format date and time to readable string
 * @param {string} dateString - Date string to format
 * @returns {string} - Formatted date and time
 */
export const formatDateTime = (dateString) => {
  if (!dateString) return 'N/A';
  
  return new Date(dateString).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

/**
 * Format phone number
 * @param {string} phone - Phone number to format
 * @returns {string} - Formatted phone number
 */
export const formatPhone = (phone) => {
  if (!phone) return 'N/A';
  
  // Remove all non-digit characters
  const cleaned = phone.replace(/\D/g, '');
  
  // Format based on length
  if (cleaned.length === 10) {
    return `+91 ${cleaned.slice(0, 5)} ${cleaned.slice(5)}`;
  } else if (cleaned.length === 12 && cleaned.startsWith('91')) {
    return `+${cleaned.slice(0, 2)} ${cleaned.slice(2, 7)} ${cleaned.slice(7)}`;
  }
  
  return phone;
};

/**
 * Truncate text to specified length
 * @param {string} text - Text to truncate
 * @param {number} maxLength - Maximum length
 * @returns {string} - Truncated text
 */
export const truncateText = (text, maxLength = 50) => {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength) + '...';
};

/**
 * Generate reference ID
 * @param {string} prefix - Prefix for the reference ID
 * @returns {string} - Generated reference ID
 */
export const generateReferenceId = (prefix = 'REF') => {
  const timestamp = Date.now();
  const random = Math.random().toString(36).substr(2, 5).toUpperCase();
  return `${prefix}-${timestamp}-${random}`;
};

/**
 * Validate email format
 * @param {string} email - Email to validate
 * @returns {boolean} - Whether email is valid
 */
export const validateEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Validate phone number format
 * @param {string} phone - Phone number to validate
 * @returns {boolean} - Whether phone number is valid
 */
export const validatePhone = (phone) => {
  const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
  return phoneRegex.test(phone.replace(/\D/g, ''));
};

/**
 * Calculate statistics from data array
 * @param {Array} data - Data array
 * @param {string} key - Key to count by (supports dot notation for nested properties)
 * @returns {Object} - Statistics object
 */
export const calculateStats = (data, key) => {
  if (!Array.isArray(data) || data.length === 0) {
    return {};
  }
  
  return data.reduce((acc, item) => {
    let value = 'Unknown';
    
    // Handle nested properties with dot notation
    if (key.includes('.')) {
      const keys = key.split('.');
      value = keys.reduce((obj, k) => obj && obj[k], item);
    } else {
      value = item[key];
    }
    
    value = value || 'Unknown';
    acc[value] = (acc[value] || 0) + 1;
    return acc;
  }, {});
};

/**
 * Filter data by search term
 * @param {Array} data - Data array to filter
 * @param {string} searchTerm - Search term
 * @param {Array} searchFields - Fields to search in
 * @returns {Array} - Filtered data
 */
export const filterDataBySearch = (data, searchTerm, searchFields = []) => {
  if (!searchTerm) return data;
  
  const term = searchTerm.toLowerCase();
  return data.filter(item =>
    searchFields.some(field => {
      const value = item[field];
      return value && value.toString().toLowerCase().includes(term);
    })
  );
};
